import React, { useState, useEffect } from 'react';
import { Card, Col, Container, Row, Table, Modal, Button, Form, Dropdown, Badge, Offcanvas } from 'react-bootstrap';
import BreadCrumb from 'Common/BreadCrumb';
import Swal from 'sweetalert2';
import axios from 'axios';



interface Question {
  id: number;
  judul: string;
  category: number;
  jumlah_soal: number;
  shuffle_questions: number | null;
  shuffle_answer: number | null;
  max_show: number;
  deleted: number;
  updated_at: string;
}

interface SoalEditFormProps {
  soal: any;
  index: number;
  isLast: boolean;
  isLoading: boolean;
  onChange: (soalId: number | string, updatedData: any) => void;
  onDelete: () => void;
}

const SoalEditForm: React.FC<SoalEditFormProps> = ({ soal, index, isLast, isLoading, onChange, onDelete }) => {
  const [formData, setFormData] = useState({
    master_soal: soal.master_soal || '',
    tipe_input: soal.tipe_input || 'option',
    jawaban_options: soal.jawaban_options?.map((opt: any) => ({
      id: opt.id,
      jawaban: opt.jawaban,
      point: opt.point
    })) || []
  });

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleJawabanChange = (optIndex: number, field: string, value: string | number) => {
    setFormData(prev => ({
      ...prev,
      jawaban_options: prev.jawaban_options.map((opt: any, i: number) => 
        i === optIndex ? { ...opt, [field]: value } : opt
      )
    }));
  };

  const handleAddOption = () => {
    setFormData(prev => ({
      ...prev,
      jawaban_options: [...prev.jawaban_options, {
        id: null,
        jawaban: '',
        point: 0
      }]
    }));
  };

  const handleRemoveOption = (optIndex: number) => {
    setFormData(prev => ({
      ...prev,
      jawaban_options: prev.jawaban_options.filter((_: any, i: number) => i !== optIndex)
    }));
  };

  // Notify parent of changes
  React.useEffect(() => {
    onChange(soal.id, formData);
  }, [formData, soal.id, onChange]);

  return (
    <div className={`mb-4 ${!isLast ? '' : ''}`}>
      {/* Google Forms Style Card */}
      <div className="card border-0 shadow-sm" style={{borderRadius: '12px'}}>
        <div className="card-body p-4">
          {/* Header with Question Number and Delete */}
          <div className="d-flex justify-content-between align-items-start mb-4">
            <div className="d-flex align-items-center gap-3">
              <div 
                className={`${soal.isNew ? 'bg-success' : 'bg-primary'} text-white rounded-circle d-flex align-items-center justify-content-center fw-bold`}
                style={{width: '32px', height: '32px', fontSize: '14px'}}
              >
                {soal.isNew ? '+' : index + 1}
              </div>
              <div>
                <h6 className="mb-0 text-dark">
                  {soal.isNew ? 'Soal Baru' : `Pertanyaan ${index + 1}`}
                  {soal.isNew && <Badge bg="success" className="ms-2 small">BARU</Badge>}
                </h6>
                <small className="text-muted">
                  {soal.isNew ? 'Belum disimpan' : `ID: ${soal.id}`}
                </small>
              </div>
            </div>
            <Button
              variant="light"
              size="sm"
              onClick={onDelete}
              disabled={isLoading}
              className="text-danger border-0"
              style={{borderRadius: '8px'}}
            >
              <i className="ri-delete-bin-line"></i>
            </Button>
          </div>

          {/* Question Input - Google Forms Style */}
          <div className="mb-4">
            <Form.Control
              as="textarea"
              rows={3}
              name="master_soal"
              placeholder="Tulis pertanyaan Anda di sini..."
              value={formData.master_soal}
              onChange={handleInputChange}
              disabled={isLoading}
              className="border-0 border-bottom rounded-0 px-0 fs-5 fw-medium"
              data-question-id={soal.id}
              style={{
                boxShadow: 'none',
                borderBottomColor: formData.master_soal ? '#1a73e8' : '#dadce0',
                borderBottomWidth: formData.master_soal ? '2px' : '1px',
                resize: 'none',
                backgroundColor: 'transparent'
              }}
            />
          </div>

          {/* Answer Options - Google Forms Style */}
          {formData.tipe_input === 'option' && (
            <div>
              {formData.jawaban_options.map((option: any, optIndex: number) => (
                <div key={optIndex} className="mb-3">
                  <div className="d-flex align-items-center gap-3">
                    {/* Option Letter */}
                    <div 
                      className={`rounded-circle d-flex align-items-center justify-content-center border-2 ${
                        option.point > 0 
                          ? 'border-success text-success bg-success bg-opacity-10' 
                          : 'border-secondary text-secondary'
                      }`}
                      style={{width: '28px', height: '28px', fontSize: '12px', fontWeight: '600'}}
                    >
                      {String.fromCharCode(65 + optIndex)}
                    </div>

                    {/* Answer Input */}
                    <div className="flex-grow-1">
                      <Form.Control
                        type="text"
                        placeholder={`Opsi ${String.fromCharCode(65 + optIndex)}`}
                        value={option.jawaban}
                        onChange={(e) => handleJawabanChange(optIndex, 'jawaban', e.target.value)}
                        disabled={isLoading}
                        className="border-0 border-bottom rounded-0 px-0"
                        style={{
                          boxShadow: 'none',
                          borderBottomColor: option.jawaban ? '#1a73e8' : '#dadce0',
                          borderBottomWidth: option.jawaban ? '2px' : '1px',
                          backgroundColor: 'transparent'
                        }}
                      />
                    </div>

                    {/* Point Input */}
                    <div style={{width: '90px'}}>
                      <div className="input-group">
                        <Form.Control
                          type="number"
                          min="0"
                          max="100"
                          placeholder="0"
                          value={option.point}
                          onChange={(e) => handleJawabanChange(optIndex, 'point', parseInt(e.target.value) || 0)}
                          disabled={isLoading}
                          className="text-center border-0"
                          style={{
                            backgroundColor: '#f8f9fa',
                            fontSize: '13px',
                            borderTopLeftRadius: '6px',
                            borderBottomLeftRadius: '6px'
                          }}
                        />
                        <span 
                          className="input-group-text bg-light border-0 text-muted small"
                          style={{
                            borderTopRightRadius: '6px',
                            borderBottomRightRadius: '6px',
                            fontSize: '12px',
                            padding: '0.375rem 0.5rem'
                          }}
                        >
                          pt
                        </span>
                      </div>
                    </div>

                    {/* Remove Button */}
                    <Button
                      variant="light"
                      size="sm"
                      onClick={() => handleRemoveOption(optIndex)}
                      disabled={isLoading || formData.jawaban_options.length <= 1}
                      className="text-danger border-0"
                      style={{borderRadius: '50%', width: '32px', height: '32px'}}
                    >
                      <i className="ri-close-line" style={{fontSize: '14px'}}></i>
                    </Button>
                  </div>

                  {/* Correct Answer Indicator */}
                  {option.point > 0 && (
                    <div className="ms-5 ps-2 mt-2">
                      <small className="text-success d-flex align-items-center">
                        <i className="ri-check-double-line me-2"></i>
                        Jawaban benar (+{option.point} poin)
                      </small>
                    </div>
                  )}
                </div>
              ))}

              {/* Add Option Button - Google Forms Style */}
              <div className="mt-4">
                <Button
                  variant="light"
                  onClick={handleAddOption}
                  disabled={isLoading || formData.jawaban_options.length >= 6}
                  className="d-flex align-items-center gap-2 border-0 text-primary bg-primary bg-opacity-10"
                  style={{borderRadius: '8px'}}
                >
                  <i className="ri-add-line"></i>
                  Tambah opsi
                </Button>
              </div>

              {/* Summary Info */}
              <div className="mt-4 pt-3 border-top">
                <div className="d-flex justify-content-between align-items-center">
                  <div className="d-flex gap-4">
                    <span className="small text-muted">
                      <i className="ri-list-check me-1"></i>
                      {formData.jawaban_options.length} opsi
                    </span>
                    <span className="small text-success">
                      <i className="ri-check-double-line me-1"></i>
                      {formData.jawaban_options.filter((opt: any) => opt.point > 0).length} benar
                    </span>
                    <span className="small text-primary">
                      <i className="ri-trophy-line me-1"></i>
                      {formData.jawaban_options.reduce((sum: number, opt: any) => sum + (opt.point || 0), 0)} poin
                    </span>
                  </div>
                  
                  {/* Validation Status */}
                  <div>
                    {formData.jawaban_options.length < 2 && (
                      <Badge bg="warning" className="small">Minimal 2 opsi</Badge>
                    )}
                    {formData.jawaban_options.length >= 2 && formData.jawaban_options.filter((opt: any) => opt.point > 0).length === 0 && (
                      <Badge bg="warning" className="small">Perlu jawaban benar</Badge>
                    )}
                    {formData.jawaban_options.length >= 2 && formData.jawaban_options.filter((opt: any) => opt.point > 0).length > 0 && (
                      <Badge bg="success" className="small">
                        <i className="ri-check-line me-1"></i>
                        Siap
                      </Badge>
                    )}
                  </div>
                </div>
              </div>

              {/* Empty State */}
              {formData.jawaban_options.length === 0 && (
                <div className="text-center py-4 border rounded-3 bg-light">
                  <i className="ri-add-box-line text-muted mb-2 d-block" style={{fontSize: '2rem'}}></i>
                  <p className="text-muted mb-0">Klik "Tambah opsi" untuk membuat pilihan jawaban</p>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

const Questions = () => {
  document.title = 'Test Settings | CITY SERVICE';

  const [showModal, setShowModal] = useState(false);
  const [formData, setFormData] = useState({
    judul: '',
    shuffle_questions: 0,
    shuffle_answer: 0,
    max_show: 1,
    deleted: 0,
  });
  const [isLoading, setIsLoading] = useState(false);
  const [tableData, setTableData] = useState<Question[]>([]);
  const [editMode, setEditMode] = useState(false);
  const [editId, setEditId] = useState<number | null>(null);
  const [showOffcanvas, setShowOffcanvas] = useState(false);
  const [selectedTest, setSelectedTest] = useState<Question | null>(null);
  const [soalList, setSoalList] = useState<any[]>([]);
  const [loadingSoal, setLoadingSoal] = useState(false);
  const [soalChanges, setSoalChanges] = useState<{[key: number]: any}>({});
  




  const handleShowModal = () => {
    setEditMode(false);
    setEditId(null);
    setFormData({
      judul: '',
      shuffle_questions: 0,
      shuffle_answer: 0,
      max_show: 1,
      deleted: 0,
    });
    setShowModal(true);
  };

  const handleShowEditModal = (question: Question) => {
    setEditMode(true);
    setEditId(question.id);
    setFormData({
      judul: question.judul,
      shuffle_questions: question.shuffle_questions || 0,
      shuffle_answer: question.shuffle_answer || 0,
      max_show: question.max_show,
      deleted: question.deleted,
    });
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setEditMode(false);
    setEditId(null);
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { id, value, type, checked } = e.target as HTMLInputElement;
    setFormData({ 
      ...formData, 
      [id]: type === 'checkbox' ? (checked ? 1 : 0) : 
            id === 'deleted' || id === 'max_show' ? parseInt(value) : 
            value 
    });
  };

  const handleFormSubmit = async () => {
    if (!formData.judul.trim()) {
      Swal.fire('Error', 'Please fill in all required fields.', 'error');
      return;
    }

    setIsLoading(true);
    Swal.fire({
      title: editMode ? 'Updating...' : 'Saving...',
      allowOutsideClick: false,
      didOpen: () => {
        Swal.showLoading();
      },
    });

    try {
      const token = localStorage.getItem('authToken');
      if (!token) {
        throw new Error('Authentication token is missing. Please log in again.');
      }

      const requestData = {
        judul: formData.judul,
        shuffle_questions: formData.shuffle_questions,
        shuffle_answer: formData.shuffle_answer,
        max_show: formData.max_show,
        deleted: 0,
      };

      let response;
      if (editMode && editId) {
        response = await axios.put(
          `/master-test/${editId}`,
          requestData,
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        );
      } else {
        response = await axios.post(
          '/master-test',
          requestData,
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        );
      }

      Swal.fire(
        'Success', 
        `Test Setting ${editMode ? 'updated' : 'added'} successfully!`, 
        'success'
      );
      setShowModal(false);
      setFormData({ 
        judul: '',
        shuffle_questions: 0,
        shuffle_answer: 0,
        max_show: 1,
        deleted: 0 
      });
      fetchQuestions();
    } catch (error) {
      Swal.fire(
        'Error',
        (axios.isAxiosError(error) && error.response?.data?.message) || 
        (error instanceof Error && error.message) || 
        'An unknown error occurred',
        'error'
      );
    } finally {
      setIsLoading(false);
    }
  };

  const fetchQuestions = async () => {
    setIsLoading(true);
    Swal.fire({
      title: 'Loading...',
      html: 'Please wait while we fetch the data.',
      timerProgressBar: true,
      allowOutsideClick: false,
      didOpen: () => {
        Swal.showLoading();
      },
    });

    try {
      const token = localStorage.getItem('authToken');
      if (!token) {
        throw new Error('Authentication token is missing. Please log in again.');
      }

      const response = await axios.get('/master-test', {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      setTableData(response.data.data || []);
      Swal.fire({
        icon: 'success',
        title: 'Data Loaded',
        showConfirmButton: false,
        timer: 1000,
      });
    } catch (error) {
      Swal.fire(
        'Error',
        (axios.isAxiosError(error) && error.response?.data?.message) || 
        (error instanceof Error && error.message) || 
        'Failed to fetch questions',
        'error'
      );
    } finally {
      setIsLoading(false);
    }
  };

  const handleDelete = async (id: number) => {
    Swal.fire({
      title: 'Are you sure?',
      text: 'You won\'t be able to revert this!',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#3085d6',
      cancelButtonColor: '#d33',
      confirmButtonText: 'Yes, delete it!',
    }).then(async (result) => {
      if (result.isConfirmed) {
        setIsLoading(true);
        try {
          const token = localStorage.getItem('authToken');
          if (!token) {
            throw new Error('Authentication token is missing. Please log in again.');
          }

          await axios.patch(`/master-test/${id}/delete`, null, {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          });

          Swal.fire('Deleted!', 'The test setting has been deleted.', 'success');
          setTableData((prevData) => prevData.filter((item) => item.id !== id));
        } catch (error) {
          Swal.fire(
            'Error',
            (axios.isAxiosError(error) && error.response?.data?.message) || 
            (error instanceof Error && error.message) || 
            'Failed to delete test setting',
            'error'
          );
        } finally {
          setIsLoading(false);
        }
      }
    });
  };

  const handleToggleShuffleQuestions = async (id: number, currentValue: number | null) => {
    if (currentValue === null) return;
    
    setIsLoading(true);
    Swal.fire({
      title: 'Updating...',
      allowOutsideClick: false,
      didOpen: () => {
        Swal.showLoading();
      },
    });

    try {
      const token = localStorage.getItem('authToken');
      if (!token) {
        throw new Error('Authentication token is missing. Please log in again.');
      }

      const newValue = currentValue === 1 ? 0 : 1;
      await axios.patch(
        `/master-test/${id}`,
        { shuffle_questions: newValue },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      setTableData((prevData) =>
        prevData.map((item) =>
          item.id === id ? { ...item, shuffle_questions: newValue } : item
        )
      );
      Swal.fire('Success', 'Shuffle questions setting has been updated.', 'success');
    } catch (error) {
      Swal.fire(
        'Error',
        (axios.isAxiosError(error) && error.response?.data?.message) || 
        (error instanceof Error && error.message) || 
        'Failed to update shuffle questions setting',
        'error'
      );
    } finally {
      setIsLoading(false);
    }
  };

  const handleToggleShuffleAnswer = async (id: number, currentValue: number | null) => {
    if (currentValue === null) return;
    
    setIsLoading(true);
    Swal.fire({
      title: 'Updating...',
      allowOutsideClick: false,
      didOpen: () => {
        Swal.showLoading();
      },
    });

    try {
      const token = localStorage.getItem('authToken');
      if (!token) {
        throw new Error('Authentication token is missing. Please log in again.');
      }

      const newValue = currentValue === 1 ? 0 : 1;
      await axios.patch(
        `/master-test/${id}`,
        { shuffle_answer: newValue },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      setTableData((prevData) =>
        prevData.map((item) =>
          item.id === id ? { ...item, shuffle_answer: newValue } : item
        )
      );
      Swal.fire('Success', 'Shuffle answer setting has been updated.', 'success');
    } catch (error) {
      Swal.fire(
        'Error',
        (axios.isAxiosError(error) && error.response?.data?.message) || 
        (error instanceof Error && error.message) || 
        'Failed to update shuffle answer setting',
        'error'
      );
    } finally {
      setIsLoading(false);
    }
  };

  const handleAddQuestions = (testId: number, testTitle: string) => {
    const test = tableData.find(item => item.id === testId);
    if (test) {
      setSelectedTest(test);
      setShowOffcanvas(true);
      fetchSoalList(testId);
    }
  };

  const handleCloseOffcanvas = () => {
    setShowOffcanvas(false);
    setSelectedTest(null);
    setSoalList([]);
  };

  const fetchSoalList = async (testId: number) => {
    setLoadingSoal(true);
    try {
      const token = localStorage.getItem('authToken');
      if (!token) {
        throw new Error('Authentication token is missing. Please log in again.');
      }

      const response = await axios.get(`/master-test/${testId}/soal`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      setSoalList(response.data.data || []);
    } catch (error) {
      console.error('Error fetching soal:', error);
      Swal.fire(
        'Error',
        (axios.isAxiosError(error) && error.response?.data?.message) || 
        (error instanceof Error && error.message) || 
        'Failed to fetch soal list',
        'error'
      );
      setSoalList([]);
    } finally {
      setLoadingSoal(false);
    }
  };

  const handleAddSoal = () => {
    if (!selectedTest) return;

    // Create a temporary new question object with empty format
    const newSoal = {
      id: `temp_${Date.now()}`, // Temporary ID for new questions
      master_soal: '',
      tipe_input: 'option',
      jawaban_options: [
        {
          id: null,
          jawaban: '',
          point: 0
        },
        {
          id: null,
          jawaban: '',
          point: 0
        }
      ],
      isNew: true // Flag to identify new questions
    };

    // Add to the end of soal list
    setSoalList(prev => [...prev, newSoal]);

    // Scroll to bottom to show the new question and auto focus
    setTimeout(() => {
      const scrollContainer = document.querySelector('[style*="maxHeight: 600px"]');
      if (scrollContainer) {
        scrollContainer.scrollTop = scrollContainer.scrollHeight;
      }
      
      // Auto focus on the new question text field
      const newQuestionTextarea = document.querySelector(`textarea[data-question-id="${newSoal.id}"]`);
      if (newQuestionTextarea) {
        (newQuestionTextarea as HTMLTextAreaElement).focus();
      }
    }, 200);
  };



  const handleSoalChange = (soalId: number | string, updatedData: any) => {
    setSoalChanges(prev => ({
      ...prev,
      [soalId]: updatedData
    }));
  };

  const handleSaveAllChanges = async () => {
    if (!selectedTest) return;

    // Separate new questions from existing ones
    const changedSoalIds = Object.keys(soalChanges);
    const newQuestions: any[] = [];
    const existingQuestions: string[] = [];

    for (const soalId of changedSoalIds) {
      if (soalId.startsWith('temp_')) {
        newQuestions.push({ id: soalId, data: soalChanges[soalId as any] });
      } else {
        existingQuestions.push(soalId);
      }
    }

    // Validate all changes
    const allChanges = [...newQuestions.map(q => q.data), ...existingQuestions.map(id => soalChanges[id as any])];
    for (const data of allChanges) {
      if (!data.master_soal.trim()) {
        Swal.fire('Error', 'Semua pertanyaan harus diisi.', 'error');
        return;
      }
      if (data.jawaban_options.length < 2) {
        Swal.fire('Error', 'Semua soal harus memiliki minimal 2 pilihan jawaban.', 'error');
        return;
      }
      const hasCorrectAnswer = data.jawaban_options.some((opt: any) => opt.point > 0);
      if (!hasCorrectAnswer) {
        Swal.fire('Error', 'Semua soal harus memiliki minimal 1 jawaban benar.', 'error');
        return;
      }
    }

    setIsLoading(true);
    Swal.fire({
      title: 'Menyimpan semua perubahan...',
      allowOutsideClick: false,
      didOpen: () => {
        Swal.showLoading();
      },
    });

    try {
      const token = localStorage.getItem('authToken');
      if (!token) {
        throw new Error('Authentication token is missing. Please log in again.');
      }

      const promises: Promise<any>[] = [];

      // Create new questions
      newQuestions.forEach(({ data }) => {
        promises.push(
          axios.post(
            `/master-test/${selectedTest.id}/soal`,
            {
              master_soal: data.master_soal,
              tipe_input: data.tipe_input,
              jawaban_options: data.jawaban_options
            },
            {
              headers: {
                Authorization: `Bearer ${token}`,
              },
            }
          )
        );
      });

      // Update existing questions
      existingQuestions.forEach(soalId => {
        const data = soalChanges[soalId as any];
        promises.push(
          axios.put(
            `/master-test/${selectedTest.id}/soal/${soalId}`,
            {
              master_soal: data.master_soal,
              tipe_input: data.tipe_input,
              jawaban_options: data.jawaban_options
            },
            {
              headers: {
                Authorization: `Bearer ${token}`,
              },
            }
          )
        );
      });

      await Promise.all(promises);

      const totalChanges = newQuestions.length + existingQuestions.length;
      Swal.fire('Success', `${totalChanges} soal berhasil disimpan!`, 'success');
      setSoalChanges({}); // Clear changes
      fetchSoalList(selectedTest.id); // Refresh soal list
    } catch (error) {
      Swal.fire(
        'Error',
        (axios.isAxiosError(error) && error.response?.data?.message) || 
        (error instanceof Error && error.message) || 
        'Gagal menyimpan perubahan',
        'error'
      );
    } finally {
      setIsLoading(false);
    }
  };

  const handleDeleteSoal = async (soalId: number | string) => {
    if (!selectedTest) return;

    // If it's a new temporary question, just remove from list
    if (typeof soalId === 'string' && soalId.startsWith('temp_')) {
      setSoalList(prev => prev.filter(soal => soal.id !== soalId));
      setSoalChanges(prev => {
        const newChanges = { ...prev };
        delete newChanges[soalId as any];
        return newChanges;
      });
      return;
    }

    Swal.fire({
      title: 'Hapus Soal?',
      text: 'Apakah Anda yakin ingin menghapus soal ini? Tindakan ini tidak dapat dibatalkan.',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#d33',
      cancelButtonColor: '#6c757d',
      confirmButtonText: 'Ya, Hapus!',
      cancelButtonText: 'Batal'
    }).then(async (result) => {
      if (result.isConfirmed) {
        setIsLoading(true);
        Swal.fire({
          title: 'Menghapus soal...',
          allowOutsideClick: false,
          didOpen: () => {
            Swal.showLoading();
          },
        });

        try {
          const token = localStorage.getItem('authToken');
          if (!token) {
            throw new Error('Authentication token is missing. Please log in again.');
          }

          await axios.delete(`/master-test/${selectedTest.id}/soal/${soalId}`, {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          });

          Swal.fire('Berhasil!', 'Soal berhasil dihapus.', 'success');
          fetchSoalList(selectedTest.id); // Refresh soal list
        } catch (error) {
          Swal.fire(
            'Error',
            (axios.isAxiosError(error) && error.response?.data?.message) || 
            (error instanceof Error && error.message) || 
            'Gagal menghapus soal',
            'error'
          );
        } finally {
          setIsLoading(false);
        }
      }
    });
  };





  useEffect(() => {
    fetchQuestions();
  }, []);

  return (
    <React.Fragment>
      <div className="page-content">
        <Container fluid>
          <BreadCrumb pageTitle="Test Settings" title="Test Settings" />

          <Row>
            <Col lg={12}>
              <Card>
                <Card.Header>
                  <div className="d-flex justify-content-between align-items-center">
                    <h5 className="card-title mb-0">Master Test Settings</h5>
                    <div className="ms-auto">
                      <button
                        className="btn btn-dark d-flex align-items-center gap-1"
                        onClick={handleShowModal}
                        disabled={isLoading}
                      >
                        <i className="ri-add-line"></i>
                        <span>Create Test Setting</span>
                      </button>
                    </div>
                  </div>
                </Card.Header>
                <Card.Body>
                  <Table striped bordered hover responsive>
                    <thead>
                      <tr>
                        <th>#</th>
                        <th>Judul</th>
                        <th>Jumlah Soal</th>
                        <th>Max Show</th>
                        <th>Shuffle Questions</th>
                        <th>Shuffle Answer</th>
                        <th>Updated At</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {Array.isArray(tableData) && tableData.length > 0 ? (
                        tableData.map((question, index) => (
                          <tr key={question.id}>
                            <td>{index + 1}</td>
                            <td>
                              <div style={{ maxWidth: '250px' }}>
                                {question.judul?.length > 40 
                                  ? `${question.judul.substring(0, 40)}...`
                                  : question.judul || '-'
                                }
                              </div>
                            </td>
                            <td>
                              <Badge bg="info">{question.jumlah_soal || 0}</Badge>
                            </td>
                            <td>
                              <Badge bg="success">{question.max_show || 0}</Badge>
                            </td>
                            <td>
                              <div className="form-check form-switch">
                                <Form.Check
                                  type="checkbox"
                                  role="switch"
                                  id={`shuffle-questions-${question.id}`}
                                  checked={question.shuffle_questions === 1}
                                  onChange={() => handleToggleShuffleQuestions(question.id, question.shuffle_questions)}
                                  disabled={isLoading || question.shuffle_questions === null}
                                />
                              </div>
                            </td>
                            <td>
                              <div className="form-check form-switch">
                                <Form.Check
                                  type="checkbox"
                                  role="switch"
                                  id={`shuffle-answer-${question.id}`}
                                  checked={question.shuffle_answer === 1}
                                  onChange={() => handleToggleShuffleAnswer(question.id, question.shuffle_answer)}
                                  disabled={isLoading || question.shuffle_answer === null}
                                />
                              </div>
                            </td>
                            <td>
                              <small className="text-muted">
                                {new Date(question.updated_at).toLocaleDateString()}
                              </small>
                            </td>
                            <td>
                              <div className="d-flex gap-2">
                                <Button
                                  variant="primary"
                                  size="sm"
                                  onClick={() => handleAddQuestions(question.id, question.judul)}
                                  disabled={isLoading}
                                  title="Tambah Soal"
                                >
                                  <i className="ri-add-line me-1"></i>
                                  Soal
                                </Button>
                                <Dropdown>
                                  <Dropdown.Toggle 
                                    variant="dark" 
                                    id={`action-dropdown-${question.id}`} 
                                    className="btn-sm"
                                    disabled={isLoading}
                                  >
                                    <i className="ri-more-2-fill"></i>
                                  </Dropdown.Toggle>
                                  <Dropdown.Menu>
                                    <Dropdown.Item onClick={() => handleShowEditModal(question)}>
                                      <i className="ri-edit-line me-2"></i> Edit
                                    </Dropdown.Item>
                                    <Dropdown.Item onClick={() => handleDelete(question.id)}>
                                      <i className="ri-delete-bin-line me-2"></i> Delete
                                    </Dropdown.Item>
                                  </Dropdown.Menu>
                                </Dropdown>
                              </div>
                            </td>
                          </tr>
                        ))
                      ) : (
                        <tr>
                          <td colSpan={8} className="text-center">
                            No test settings available
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </Table>
                </Card.Body>
              </Card>
            </Col>
          </Row>
        </Container>

        {/* Modal for Adding/Editing Question */}
        <Modal show={showModal} onHide={handleCloseModal} backdrop="static" centered size="lg">
          <Modal.Header closeButton>
            <Modal.Title>{editMode ? 'Edit Test Setting' : 'Add Test Setting'}</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <Form>
              <Form.Group className="mb-3">
                <Form.Label htmlFor="judul">Judul <span className="text-danger">*</span></Form.Label>
                <Form.Control
                  type="text"
                  id="judul"
                  placeholder="Enter the title"
                  value={formData.judul}
                  onChange={handleInputChange}
                  disabled={isLoading}
                  isInvalid={!formData.judul.trim()}
                />
                <Form.Control.Feedback type="invalid">
                  Judul is required.
                </Form.Control.Feedback>
              </Form.Group>



              <Form.Group className="mb-3">
                <Form.Label htmlFor="max_show">Max Show</Form.Label>
                <Form.Control
                  type="number"
                  id="max_show"
                  placeholder="Enter max show questions"
                  min="1"
                  value={formData.max_show}
                  onChange={handleInputChange}
                  disabled={isLoading}
                />
              </Form.Group>

              <Row>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Shuffle Questions</Form.Label>
                    <div className="form-check form-switch mt-2">
                      <Form.Check
                        type="checkbox"
                        role="switch"
                        id="shuffle_questions"
                        label="Enable Shuffle Questions"
                        checked={formData.shuffle_questions === 1}
                        onChange={handleInputChange}
                        disabled={isLoading}
                      />
                    </div>
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Shuffle Answer</Form.Label>
                    <div className="form-check form-switch mt-2">
                      <Form.Check
                        type="checkbox"
                        role="switch"
                        id="shuffle_answer"
                        label="Enable Shuffle Answer"
                        checked={formData.shuffle_answer === 1}
                        onChange={handleInputChange}
                        disabled={isLoading}
                      />
                    </div>
                  </Form.Group>
                </Col>
              </Row>
            </Form>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={handleCloseModal} disabled={isLoading}>
              Cancel
            </Button>
            <Button variant="dark" onClick={handleFormSubmit} disabled={isLoading}>
              {isLoading ? (editMode ? 'Updating...' : 'Saving...') : (editMode ? 'Update' : 'Save')}
            </Button>
          </Modal.Footer>
        </Modal>

        {/* Offcanvas for Test Details */}
        <Offcanvas 
          show={showOffcanvas} 
          onHide={handleCloseOffcanvas} 
          placement="end" 
          backdrop={true} 
          style={{width: '900px'}}
        >
          <Offcanvas.Header closeButton>
            <Offcanvas.Title>
              <i className="ri-file-list-3-line me-2"></i>
              Detail Test
            </Offcanvas.Title>
          </Offcanvas.Header>
          <Offcanvas.Body>
            {selectedTest && (
              <>
                {/* Header Info */}
                <div className="border-bottom pb-3 mb-3">
                  <h5 className="mb-1">{selectedTest.judul}</h5>
                  <div className="d-flex justify-content-between align-items-center text-sm">
                    <span className="text-muted">ID: {selectedTest.id}</span>
                    <span className="text-muted">{new Date(selectedTest.updated_at).toLocaleDateString('id-ID')}</span>
                  </div>
                </div>

                {/* Quick Stats */}
                <div className="mb-3">
                  <div className="d-flex justify-content-between text-sm">
                    <span>Total: <strong>{selectedTest.jumlah_soal || 0}</strong></span>
                    <span>Max Show: <strong>{selectedTest.max_show || 0}</strong></span>
                    <span>Shuffle Q: <strong>{selectedTest.shuffle_questions === 1 ? 'On' : 'Off'}</strong></span>
                    <span>Shuffle A: <strong>{selectedTest.shuffle_answer === 1 ? 'On' : 'Off'}</strong></span>
                  </div>
                </div>

                {/* Soal List */}
                <div className="d-flex justify-content-between align-items-center mb-2">
                  <h6 className="mb-0">Daftar Soal ({soalList.length})</h6>
                </div>
                
                {loadingSoal ? (
                  <div className="text-center py-3">
                    <div className="spinner-border spinner-border-sm me-2"></div>
                    <span className="text-muted">Memuat...</span>
                  </div>
                ) : soalList.length > 0 ? (
                  <div style={{maxHeight: '600px', overflowY: 'auto'}}>
                    {soalList.map((soal, index) => (
                      <SoalEditForm 
                        key={soal.id || index}
                        soal={soal}
                        index={index}
                        isLast={index === soalList.length - 1}
                        isLoading={isLoading}
                        onChange={handleSoalChange}
                        onDelete={() => handleDeleteSoal(soal.id)}
                      />
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-4 text-muted">
                    <i className="ri-file-list-line mb-2 d-block" style={{fontSize: '2rem'}}></i>
                    <p className="mb-0">Belum ada soal</p>
                  </div>
                )}

              </>
            )}
          </Offcanvas.Body>
          
          {/* Footer with Action Buttons */}
          {selectedTest && (
            <div className="border-top bg-white p-3">
              <Row className="g-2">
                <Col xs={6}>
                  <Button 
                    variant="success" 
                    disabled={isLoading || loadingSoal || Object.keys(soalChanges).length === 0}
                    className="w-100 py-2 fw-medium"
                    onClick={handleSaveAllChanges}
                  >
                    <i className="ri-save-line me-2"></i>
                    Simpan Semua {Object.keys(soalChanges).length > 0 && `(${Object.keys(soalChanges).length})`}
                  </Button>
                </Col>
                <Col xs={6}>
                  <Button 
                    variant="primary" 
                    disabled={isLoading || loadingSoal}
                    className="w-100 py-2 fw-medium"
                    onClick={handleAddSoal}
                  >
                    <i className="ri-add-circle-line me-2"></i>
                    Tambah Soal
                  </Button>
                </Col>
              </Row>
              
              {Object.keys(soalChanges).length > 0 && (
                <div className="mt-2">
                  <small className="text-muted">
                    <i className="ri-information-line me-1"></i>
                    {Object.keys(soalChanges).length} soal memiliki perubahan yang belum disimpan
                  </small>
                </div>
              )}
            </div>
          )}
        </Offcanvas>


      </div>
    </React.Fragment>
  );
};

export default Questions;
